import { useAuthContext } from 'contexts';
import type { RevalidateTrigger } from 'data/FHIRFetcher';
import { ACTIVITY_TAB } from 'fhir-sdoh/common/constants';
import moment from 'moment';
import { useEffect, useState } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import type { SemanticDatepickerProps } from 'react-semantic-ui-datepickers/dist/types';
import { Button, Form, Modal, Select } from 'semantic-ui-react';
import type { Option, ReferralSummary } from 'types';

import { FiltersGrid, DatePickerWrapper, ResponsiveModalContent, ResponsiveForm, DueDateLabel } from './filters.styles';
import type { DefaultOptionType } from './taskHeader/types';

import type { TaskFilterState } from './index';


/**
 * What the filter modal accepts. Its only call site is the `<TaskFilters>` render
 * inside `<TaskHeader>` in `./index`, so each field below is the type that component
 * holds for it.
 */
type TaskFiltersProps = {
  /** Which activity tab is mounted; absent on the per-member panel. */
  activeitem?: string | undefined;
  /** True on the activity tabs, absent on the per-member panel. */
  activity?: boolean | undefined;
  datevalue: Date[] | null;
  foodVendor: Option[];
  handleFilterTaskToggle: (value: boolean) => void;
  isTaskFilterOpen: boolean;
  patientId?: string | undefined;
  /** Gates the practitioner picker; false on the per-member task tab. */
  practfilter?: boolean | undefined;
  practlist: DefaultOptionType[];
  referralIdList: ReferralSummary[];
  resetFilters: () => void;
  retriveFilterFromLocalStorage: () => void;
  selectedPayerDropdown?: string | undefined;
  setDateValue: (value: Date[] | null) => void;
  setLoader: (value: boolean) => void;
  setRenderURL: (value: boolean) => void;
  /**
   * The parent's `useState` setter. Typed as the `Dispatch` it is rather than a
   * plain callback, because the parent's own `resetFilters` and this modal's submit
   * both hand it a value while other call sites use the updater form.
   */
  setState: React.Dispatch<React.SetStateAction<TaskFilterState>>;
  state: TaskFilterState;
  tasksMutate: RevalidateTrigger;
};

function TaskFilters(props: TaskFiltersProps) {
  const authInfo = useAuthContext();
  const vendorList = props.foodVendor;
  const [disable, setDisable] = useState(false);
  //below is the tempState and will pass over to props once the filter request has been submitted.
  /** Filter name -> query fragment; the keys are the Dropdown `name`s below. */
  const [tempState, setTempState] = useState<TaskFilterState>(props.state);
  const { category, status, practitioner, vendor, referral, subCategory, dateStatus, period, priority, program } =
    tempState;
  useEffect(() => {
    if (
      category ||
      status ||
      practitioner ||
      vendor ||
      referral ||
      subCategory ||
      dateStatus ||
      period ||
      priority ||
      program
    ) {
      setDisable(false);
    } else {
      setDisable(true);
    }

     
  }, [tempState]);

  useEffect(() => {
    if (props.state) {
      setTempState(props.state);
    }
  }, [props.state]);

  const taskCategories = [
    'automated',
    'careplan',
    'communication',
    'confirm-food-card-balance',
    'consultation',
    'other-goods-delivery',
    'education',
    'enrollment-action',
    'food-box',
    'food-card',
    'food-delivery',
    'food-recall',
    'member',
    'outreach',
    'survey',
    'touchpoint',
    'triage',
    'voicemail',
  ];
  const taskDueDateStatus = ['pastdue', 'today', 'future'];
  const referralFilter = props?.referralIdList;
  /** `react-semantic-ui-datepickers` in range mode: `value` is `[start, end]`, or null when cleared. */
  const handleDateChange = (
    // `SemanticDatepickerProps['onChange']` declares the event as
    // `SyntheticEvent<Element>`, not `SyntheticEvent<HTMLElement>`; only
    // `preventDefault` is used, which `Element` already carries.
    e: React.SyntheticEvent<Element> | undefined,
    selectprops: { name: string; value: Date[] | null }
  ) => {
    e?.preventDefault();
    const name = selectprops.name;
    //clear the period back to null when click the clear due date buttom
    if (!selectprops.value) {
      setTempState((prevState: TaskFilterState) => ({ ...prevState, [name]: null }));
      return;
    }
    const startDay = selectprops?.value?.[0];
    const endDay = selectprops?.value?.[1];
    let query = '';
    const ge = moment(startDay).startOf('day').format('YYYY-MM-DD');
    if (endDay) {
      const le = moment(endDay ? endDay : startDay)
        .endOf('day')
        .format('YYYY-MM-DD');
      // query = `&period=ge` + ge + `&period=le` + le;
      query = `&period=ge` + ge + `:::le` + le;
    } else {
      query = `&period=eq` + ge;
    }
    setTempState((prevState: TaskFilterState) => ({ ...prevState, [name]: query }));
    props.setDateValue(selectprops.value);
  };
  const handleChange = (e: React.SyntheticEvent<HTMLElement>, selectprops: { name: string; value: string }) => {
    e.preventDefault();
    const name = selectprops.name;
    const value = selectprops.value;
    if (selectprops.name === 'date_status') {
      const today = moment().local().format('YYYY-MM-DD');
      let updatedPeriod = '';
      //upate date range when "today/pastdue/future" filter is selected.
      if (value === 'today') {
        updatedPeriod = `&period=eq${today}`;
      } else if (value === 'pastdue') {
        updatedPeriod = `&period=lt${today}`;
      } else if (value === 'future') {
        updatedPeriod = `&period=gt${today}`;
      }
      setTempState((prevState: TaskFilterState) => ({ ...prevState, period: updatedPeriod, dateStatus: value }));
    } else {
      setTempState((prevState: TaskFilterState) => ({ ...prevState, [name]: value }));
    }
    //if category is not "careplan", we want to set subCategory back to null
    /*
     * SUSPECTED DEFECT (pre-existing): `category` is destructured from
     * `tempState` above and holds the Dropdown's string value, so `.value` is
     * always `undefined` and this arm therefore fires for EVERY category rather
     * than only for non-careplan ones — clearing `subCategory` even when the
     * user picked `careplan`. The comparison is left exactly as written.
     *
     * A trailing `name !== 'subCategory'` conjunct was dropped: `name === 'category'`
     * above already pins it, so the compiler reported the comparison as one that can
     * never be false. Removing it leaves the condition's value unchanged.
     */
    if (subCategory && name === 'category' && (category as unknown as { value?: string }).value !== 'careplan') {
      setTempState((prevState: TaskFilterState) => ({ ...prevState, subCategory: null }));
    }
  };

  // `async` kept: these are DOM handlers, so dropping it changes what they return.
  // eslint-disable-next-line @typescript-eslint/require-await
  const handleSubmit = async (e?: React.SyntheticEvent<HTMLElement>) => {
    e?.preventDefault();
    props.setLoader(true);
    props.setState(tempState);
    // Fire-and-forget revalidation: the modal closes immediately and the list
    // re-renders when SWR resolves. `void` states that the promise is deliberately
    // unawaited, which is what this already did.
    void props.tasksMutate();
    props.setRenderURL(true);
    props.handleFilterTaskToggle(false);
  };

  // Save filter
  function saveFilters() {
    const filterState = JSON.stringify(tempState);
    //save date filter in local storage
    if (props.activeitem === ACTIVITY_TAB.ALL_TASKS) {
      localStorage.setItem('allFilter', filterState);
      localStorage.setItem('allDateFilter', JSON.stringify(props.datevalue ? props.datevalue : [])); //if datevalue is undefined/null, set them to empty array
    } else if (props.activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS) {
      localStorage.setItem('careTeamFilter', filterState);
      localStorage.setItem('careTeamDateFilter', JSON.stringify(props.datevalue ? props.datevalue : [])); //if datevalue is undefined/null, set them to empty array
    } else {
      localStorage.setItem('myFilter', filterState);
      localStorage.setItem('myDateFilter', JSON.stringify(props.datevalue ? props.datevalue : [])); //if datevalue is undefined/null, set them to empty array
    }
  }
  // Load last saved filter
  // eslint-disable-next-line @typescript-eslint/require-await
  const loadFilters = async (_e?: React.SyntheticEvent<HTMLElement>) => {
    props?.retriveFilterFromLocalStorage();
  };

  const resetTempFiltersFunc = () => {
    props.setDateValue(null);
    setTempState({
      category: null,
      period: null,
      status: null,
      priority: null,
      practitioner: null,
      vendor: null,
      referral: null,
      subCategory: null,
      dateStatus: null,
      program: [],
    });
  };

  const onClear = async () => {
    resetTempFiltersFunc();
    props.resetFilters();
    props.setLoader(true);
    props.setState({
      category: null,
      period: null,
      status: null,
      priority: null,
      practitioner: null,
      vendor: null,
      referral: null,
      subCategory: null,
      dateStatus: null,
      program: [],
    });
    await props.tasksMutate();
    props.setRenderURL(true);
    props.handleFilterTaskToggle(false);
  };

  // Checks if the two states are empty
  const isStateEmpty = (obj: TaskFilterState) =>
    Object.values(obj).every(
      (value) => value === null || value === undefined || (Array.isArray(value) && value.length === 0)
    );

  return (
    <>
      {!isStateEmpty(props.state) && !isStateEmpty(tempState) && (
        <Button type="button" content="Clear" basic onClick={async () => await onClear()} />
      )}
      <Modal
        style={{ width: '95%', maxWidth: 700 }}
        as={Form}
        onOpen={() => props.handleFilterTaskToggle(true)}
        onClose={() => props.handleFilterTaskToggle(false)}
        open={props.isTaskFilterOpen}
        onSubmit={handleSubmit}
        trigger={<Button icon="filter" content="Filter" size="small" />}
        verticalAlign="middle"
      >
        <Modal.Content as={ResponsiveModalContent}>
          <ResponsiveForm>
            <FiltersGrid>
              <DatePickerWrapper>
                <DueDateLabel>Due Date:</DueDateLabel>
                <Form.Field style={{ margin: '0em 0em 1em' }}>
                  <SemanticDatepicker
                    autoComplete="off"
                    value={props.datevalue}
                    /*
                     * The library hands `onChange` the whole `SemanticDatepickerProps`,
                     * whose `value` is `Date | Date[] | null` because the component also
                     * serves single-date mode. `type="range"` above pins it to the pair
                     * this handler destructures, which the prop type cannot express.
                     */
                    onChange={handleDateChange as SemanticDatepickerProps['onChange']}
                    name="period"
                    placeholder="Filter by Due Date"
                    format="YYYY-MM-DD"
                    className={dateStatus ? 'disabled field' : ''}
                    type="range"
                  />
                </Form.Field>
              </DatePickerWrapper>
              <Form.Field
                label="Task Category:"
                control={Select}
                selectOnBlur={false}
                name="category"
                placeholder="Filter by Category"
                value={category}
                options={taskCategories.map((k: string) => {
                  return { key: k, value: k, text: k.toUpperCase() };
                })}
                onChange={handleChange}
              />
              <Form.Field
                label="Status:"
                name="status"
                selectOnBlur={false}
                control={Select}
                placeholder="Filter by Status"
                value={status}
                options={['requested', 'in-progress', 'on-hold', 'completed', 'cancelled', 'failed', 'received'].map(
                  (k) => {
                    return {
                      key: k,
                      value: k,
                      text: k === 'received' ? 'OUTREACH' : k.toUpperCase(),
                    };
                  }
                )}
                onChange={handleChange}
              />
              {!props?.patientId && category && category === 'careplan' && (
                <Form.Field
                  label="Task Sub-Category:"
                  control={Select}
                  selectOnBlur={false}
                  name="subCategory"
                  placeholder="Filter by Sub-Category"
                  value={subCategory}
                  options={['assessment', 'screening', 'intervention'].map((k: string) => {
                    return { key: k, value: k, text: k.toUpperCase() };
                  })}
                  onChange={handleChange}
                />
              )}
              {props.practfilter && (
                <Form.Field
                  label="Practitioner"
                  selectOnBlur={false}
                  control={Select}
                  name="practitioner"
                  search
                  placeholder="Filter by Practitioner"
                  value={practitioner}
                  options={props.practlist}
                  onChange={handleChange}
                />
              )}
              <Form.Field
                label="Urgent?"
                name="priority"
                selectOnBlur={false}
                control={Select}
                placeholder="Filter by Priority"
                value={priority}
                options={['routine', 'asap'].map((k: string) => {
                  const map: Record<string, string> = { routine: 'standard', asap: 'high priority' };
                  return {
                    key: k,
                    value: k,
                    text: map[k]!.toUpperCase(),
                  };
                })}
                onChange={handleChange}
              />
              <Form.Input
                label="Date Status:"
                control={Select}
                selectOnBlur={false}
                name="date_status"
                placeholder="Filter by Date Status"
                value={dateStatus}
                options={taskDueDateStatus.map((k: string) => {
                  return { key: k, value: k, text: k.toUpperCase() };
                })}
                /*
                 * Every other Select in this form is a `Form.Field`; this one alone
                 * is a `Form.Input`, whose `onChange` is typed for a text input
                 * (`ChangeEvent<HTMLInputElement>`, `InputOnChangeData` — which has
                 * no `name`). `control={Select}` means a Select is what actually
                 * renders and what actually calls this handler, so the cast states
                 * the component in play rather than the one the props describe.
                 * Switching to `Form.Field` would be the real fix, but it changes
                 * which wrapper semantic-ui renders.
                 */
                onChange={handleChange as unknown as NonNullable<React.ComponentProps<typeof Form.Input>['onChange']>}
                autoComplete="off"
                className={period && !dateStatus ? 'disabled field' : ''}
              />
              {!props.activity && (
                <Form.Field
                  label="Referral:"
                  selectOnBlur={false}
                  control={Select}
                  name="referral"
                  placeholder="Filter by Referral"
                  value={referral}
                  options={referralFilter?.map((k: ReferralSummary) => {
                    return { key: k?.referralId, value: k?.referralId, text: k?.referralId?.toUpperCase() };
                  })}
                  onChange={handleChange}
                />
              )}
              <Form.Field
                label="Vendor:"
                control={Select}
                direction="upward"
                selectOnBlur={false}
                name="vendor"
                placeholder="Filter by Vendor"
                value={vendor}
                options={vendorList}
                onChange={handleChange}
              />
              {props.activity && props?.selectedPayerDropdown === 'All' && (
                <Form.Field
                  direction="upward"
                  label="Program:"
                  name="program"
                  multiple
                  clearable
                  selectOnBlur={false}
                  control={Select}
                  placeholder="Filter by Program"
                  value={program || []}
                  options={authInfo.payers || []}
                  onChange={handleChange}
                ></Form.Field>
              )}
            </FiltersGrid>
            {/* Save/Apply buttons row */}
          </ResponsiveForm>
        </Modal.Content>
        <Modal.Actions>
          {props.activity && props?.selectedPayerDropdown === 'All' && (
            <>
              <Button type="button" content="Save Filter" color="orange" basic onClick={saveFilters} />
              <Button type="button" content="Apply last saved filter" color="green" basic onClick={loadFilters} />
            </>
          )}
          <Button
            type="button"
            content="Clear"
            basic
            onClick={() => {
              props.resetFilters();
              resetTempFiltersFunc();
            }}
          />
          <Button content="Close" type="button" color="red" onClick={() => props.handleFilterTaskToggle(false)} />
          <Button type="submit" color="green" content="Filter Tasks" disabled={disable} />
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default TaskFilters;
